import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Mic, RefreshCw, Trash2, Play, Upload, Volume2, AlertTriangle,
  Sparkles, Loader2, CheckCircle2, User,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Play a small preview by hitting the topic audio endpoint using the FIRST
// affected topic id.  This is the fastest way to hear how the avatar sounds
// with its currently-selected voice.
const previewTopicAudio = (topicId) => {
  const url = `${API}/api/storyteller/topic/${topicId}/audio?lang=en`;
  const audio = new Audio(url);
  audio.crossOrigin = 'anonymous';
  audio.play().catch(() => toast.error('Could not play audio — regenerate first if you just changed the voice'));
  return audio;
};

const previewCustomSample = (avatarId) => {
  const url = `${API}/api/admin/avatars/${avatarId}/sample-preview?t=${Date.now()}`;
  fetch(url, { headers: authHeaders() })
    .then(r => r.blob())
    .then(b => {
      const audio = new Audio(URL.createObjectURL(b));
      audio.play().catch(() => toast.error('Could not play uploaded sample'));
    })
    .catch(() => toast.error('Could not load uploaded sample'));
};

// Cache the fetched sample blobs across the whole admin page so clicking a
// voice pill twice never re-hits the backend.
const voiceSampleCache = new Map();
const previewVoiceSample = async (voice) => {
  try {
    let blob = voiceSampleCache.get(voice);
    if (!blob) {
      const res = await fetch(`${API}/api/admin/avatars/voice-sample/${voice}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      blob = await res.blob();
      voiceSampleCache.set(voice, blob);
    }
    const audio = new Audio(URL.createObjectURL(blob));
    await audio.play();
  } catch (e) {
    toast.error(`Preview failed: ${e.message}`);
  }
};

const AvatarCard = ({ av, voices, onChange, refresh }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const fileRef = useRef(null);

  const saveVoice = async (voice) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/avatars/${av.avatar_id}/voice`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice, regenerate: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      toast.success(`Voice updated → ${voice}. ${j.invalidated_mp3s} cached MP3s cleared. Background regen started.`);
      onChange(av.avatar_id, voice);
    } catch (e) {
      toast.error('Failed to update voice');
    } finally {
      setSaving(false);
    }
  };

  const uploadSample = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/admin/avatars/${av.avatar_id}/sample`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const j = await res.json();
      toast.success(`Custom sample uploaded (${j.size_kb} KB). Students will now hear YOUR recording for this avatar.`);
      await refresh();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const deleteSample = async () => {
    if (!window.confirm(`Remove custom voice sample for ${av.display_name}? This avatar will revert to the OpenAI voice ${av.voice}.`)) return;
    setUploading(true);
    try {
      const res = await fetch(`${API}/api/admin/avatars/${av.avatar_id}/sample`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Custom sample removed — reverted to OpenAI voice');
      await refresh();
    } catch (e) {
      toast.error('Failed to remove sample');
    } finally {
      setUploading(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/avatars/${av.avatar_id}/regenerate`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      toast.success(`Regeneration scheduled for ${av.topic_count} topics (${j.invalidated_mp3s} cached files cleared).`);
    } catch (e) {
      toast.error('Failed to schedule regeneration');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="p-5 rounded-2xl border bg-card space-y-4" data-testid={`avatar-card-${av.avatar_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-20 h-20 shrink-0 rounded-2xl overflow-hidden bg-muted flex items-center justify-center border">
          {av.avatar_url ? (
            <img src={`${API}${av.avatar_url}`} alt={av.display_name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base truncate">{av.display_name}</h3>
          <p className="text-xs text-muted-foreground truncate">{av.avatar_id}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {av.subjects.length === 0 ? (
              <Badge variant="outline" className="text-[10px]">Fallback avatar</Badge>
            ) : av.subjects.slice(0, 4).map(s => (
              <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
            ))}
            {av.subjects.length > 4 && (
              <Badge variant="outline" className="text-[10px]">+{av.subjects.length - 4}</Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Narrates <span className="font-semibold text-foreground">{av.topic_count}</span> topics
          </p>
        </div>
      </div>

      {/* Voice selector */}
      <div className="space-y-2">
        <label className="text-xs font-semibold flex items-center gap-1.5">
          <Volume2 className="w-3.5 h-3.5" />OpenAI TTS Voice
          {av.voice !== av.default_voice && (
            <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 text-[9px]">Custom</Badge>
          )}
        </label>
        <Select value={av.voice} onValueChange={saveVoice} disabled={saving || av.has_custom_sample}>
          <SelectTrigger className="text-sm" data-testid={`voice-select-${av.avatar_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {voices.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Phase 40.14b — one tiny play-pill per voice so admins can sample
            each option BEFORE committing.  The MP3 is cached server-side. */}
        <div className="flex flex-wrap gap-1 pt-1" data-testid={`voice-pills-${av.avatar_id}`}>
          {voices.map(v => (
            <button
              key={v.id}
              type="button"
              onClick={() => previewVoiceSample(v.id)}
              disabled={av.has_custom_sample}
              data-testid={`voice-preview-${av.avatar_id}-${v.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border ${
                av.voice === v.id
                  ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800 ring-1 ring-indigo-400'
                  : 'bg-muted hover:bg-indigo-50 dark:hover:bg-indigo-950/20 border-transparent'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              title={`Preview ${v.label}`}
            >
              <Play className="w-2.5 h-2.5 fill-current" />{v.id}
            </button>
          ))}
        </div>
        {av.has_custom_sample && (
          <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />Custom sample is active — the OpenAI voice above is currently ignored.
          </p>
        )}
      </div>

      {/* Custom voice sample */}
      <div className="pt-3 border-t space-y-2">
        <label className="text-xs font-semibold flex items-center gap-1.5">
          <Mic className="w-3.5 h-3.5" />Custom Voice Sample
          {av.has_custom_sample && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
        </label>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.wav"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSample(f); }}
          data-testid={`upload-input-${av.avatar_id}`}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`upload-btn-${av.avatar_id}`}
            className="text-xs"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            {av.has_custom_sample ? 'Replace' : 'Upload MP3'}
          </Button>
          {av.has_custom_sample && (
            <>
              <Button size="sm" variant="outline" onClick={() => previewCustomSample(av.avatar_id)} className="text-xs" data-testid={`play-sample-${av.avatar_id}`}>
                <Play className="w-3.5 h-3.5 mr-1.5" />Play ({av.custom_sample_size_kb} KB)
              </Button>
              <Button size="sm" variant="outline" onClick={deleteSample} disabled={uploading} className="text-xs text-rose-600" data-testid={`delete-sample-${av.avatar_id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
              </Button>
            </>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground leading-snug">
          Record a paragraph in this character&apos;s voice, save it as MP3/M4A/WAV (≤ 10 MB) and upload here.
          Students will hear YOUR recording every time this avatar narrates — no OpenAI TTS call, zero credit spend.
        </p>
      </div>

      {/* Actions row */}
      <div className="pt-3 border-t flex flex-wrap gap-2">
        {av.topics.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => previewTopicAudio(av.topics[0].id)} className="text-xs" data-testid={`preview-topic-${av.avatar_id}`}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Preview &ldquo;{av.topics[0].title.slice(0, 24)}…&rdquo;
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={regenerate} disabled={regenerating} className="text-xs" data-testid={`regen-${av.avatar_id}`}>
          {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Regenerate ({av.topic_count})
        </Button>
      </div>
    </div>
  );
};

export const AvatarVoicesAdmin = () => {
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/avatars`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setAvatars(j.avatars || []);
      setVoices(j.voices || []);
    } catch (e) {
      toast.error('Failed to load avatars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLocalVoiceChange = (id, voice) => {
    setAvatars(list => list.map(a => a.avatar_id === id ? { ...a, voice } : a));
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="avatar-voices-admin">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mic className="w-6 h-6 text-indigo-500" />Avatar Voices
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Assign a distinct OpenAI TTS voice to every Concept Whiteboard avatar, or upload
            your own voice recording. Changes clear the cached MP3s and re-synthesise in the
            background so students immediately hear the new voice.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="refresh-avatars">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-200 flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <strong>Kill Switch:</strong> If the LLM Kill Switch is ON (System Panel), OpenAI TTS
          re-synthesis will silently skip — you&apos;ll only hear regenerated audio once the switch
          is OFF. Uploaded custom samples always play, kill-switch or not.
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="avatars-grid">
          {avatars.map(av => (
            <AvatarCard key={av.avatar_id} av={av} voices={voices} onChange={handleLocalVoiceChange} refresh={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AvatarVoicesAdmin;
